export const transmittalsArticles = [
    {
        id: 'transmittals.overview',
        title: 'Transmittals Overview',
        category: 'Transmittals',
        order: 1,
        summary: 'Sending documents to external recipients',
        content: `
Transmittals allow you to formally send documents to external recipients with tracking and confirmation.

**Key Features:**

- Package documents for sending
- Track recipient access
- Confirm receipt
- Maintain transmittal history

**Use Cases:**

- Send drawings to consultants
- Issue documents to contractors
- Distribute specifications to suppliers
    `,
        expandableSections: [
            {
                title: 'Transmittal Grid',
                content: `
The grid shows all transmittals with:

- **Number** - Transmittal reference
- **Subject** - Brief description
- **Date** - When created
- **Recipients** - Who it was sent to
- **Status** - Sent/Viewed/Downloaded

Click a transmittal to see full details and tracking.
        `,
            },
        ],
        keywords: ['transmittals', 'send', 'distribute', 'issue'],
        route: '/transmittals',
        relatedArticles: ['transmittals.create', 'documents.overview'],
    },
    {
        id: 'transmittals.create',
        title: 'Creating Transmittals',
        category: 'Transmittals',
        order: 2,
        summary: 'How to create and send document transmittals',
        content: `
Create transmittals to formally issue documents to recipients.

**Required Information:**

- Subject line
- Recipients (email addresses)
- Documents to include

**Optional:**

- Cover letter/message
- Requested action
- Due date
    `,
        expandableSections: [
            {
                title: 'Creating a Transmittal',
                content: `
1. Click **New Transmittal**
2. Enter details:
   - Title, job, folder
   - Disciplines (add several — a PO pack can mix Architectural, Structural, Safety…)
   - Purpose / reason for issue, due date
   - Cover letter and internal notes
3. Attach documents (right in the dialog):
   - **Search** by typing a few words in any order — matches document number, file name, description and revision
   - The list is scoped to the transmittal's **job** by default; untick *This job only* to search everything
   - Or click **Upload & attach** (or drag files onto the list) — files upload to the Documents module, link to the job, and attach in one step
   - Selected documents stay pinned as chips above the search, so changing the search never loses a selection
4. Click **Create**, then **Issue** when ready

Issuing generates secure links per recipient and starts the acknowledgment clock.
        `,
            },
            {
                title: 'Document Versions on Transmittals',
                content: `
A transmittal attaches a **specific version** of each document — an issued transmittal is a fixed record; uploading a new version of a drawing never silently changes what recipients already received.

**When a document is updated:**

- On a **draft** transmittal, the attached document shows a *newer version available* badge — click **Use latest** to swap it before issuing
- After uploading a new version (Documents → Versions), a prompt lists every transmittal still carrying the old version:
  - **Drafts** — one click updates them to the new version
  - **Issued** — one click creates a **revision** (e.g. T-0012-R1): a new draft carrying the latest versions. Review it, re-issue it, and recipients get fresh links and must **acknowledge again**
- The original transmittal is marked *superseded* and stays in the history

This is how changes always end in a new acknowledgment — the old record is never edited in place.
        `,
            },
            {
                title: 'Quick Send',
                content: `
From the Documents page:

1. Select documents
2. Right-click > Create Transmittal
3. Documents are pre-attached
4. Add recipients and send
        `,
            },
        ],
        keywords: ['create transmittal', 'send documents', 'new transmittal', 'attach documents', 'upload', 'document version', 'revision', 'use latest', 'superseded', 'acknowledge again'],
        route: '/transmittals',
        relatedArticles: ['transmittals.overview', 'transmittals.tracking'],
        screenshots: [
            {
                src: 'transmittals/transmittals-create.png',
                alt: 'New Transmittal dialog',
                caption: 'Creating a transmittal — choose the documents and recipients',
            },
        ],
    },
    {
        id: 'transmittals.tracking',
        title: 'Recipient Tracking',
        category: 'Transmittals',
        order: 3,
        summary: 'Tracking transmittal delivery and access',
        content: `
Track whether recipients have received, viewed, and downloaded your transmittals.

**Tracking Information:**

- Email delivered
- Link opened
- Files downloaded
- Timestamp of each action
    `,
        expandableSections: [
            {
                title: 'Viewing Tracking Status',
                content: `
1. Open a transmittal
2. Go to **Recipients** tab
3. See status for each recipient:
   - Pending (not opened)
   - Viewed (opened link)
   - Downloaded (downloaded files)

Timestamps show when each action occurred.
        `,
            },
            {
                title: 'Resending',
                content: `
If a recipient hasn't accessed the transmittal:

1. Open the transmittal
2. Select the recipient
3. Click **Resend**

A new email is sent with the same download link.
        `,
            },
            {
                title: 'Chasing Outstanding Acknowledgments',
                content: `
The **Transmittals Awaiting Acknowledgment** report (Reports screen) lists every issued transmittal that a recipient has NOT yet acknowledged — one row per outstanding recipient with:

- Recipient name/email and whether they've **viewed** or **downloaded** yet
- **Issued** and **due** dates, and **days outstanding**

Filter it to one job, group by transmittal or recipient type, and export to PDF or Excel for the follow-up round. There's also an **Email Log** report showing every email the system has sent (purchase orders, quotes, RFIs) with delivery status.
        `,
            },
        ],
        keywords: ['tracking', 'delivery', 'confirmation', 'viewed', 'downloaded', 'acknowledgment', 'outstanding', 'email log'],
        route: '/transmittals',
        relatedArticles: ['transmittals.overview', 'transmittals.create'],
    },
    {
        id: 'transmittals.folders',
        title: 'Transmittal Folders',
        category: 'Transmittals',
        order: 4,
        summary: 'Organizing transmittals into folders',
        content: `
Organize transmittals into folders for easy navigation and management.

**Folder Structure:**

Create folders by:
- Job
- Recipient type
- Document type
- Project phase
    `,
        expandableSections: [
            {
                title: 'Managing Folders',
                content: `
**Creating Folders:**
1. Click **New Folder**
2. Enter folder name
3. Save

**Moving Transmittals:**
1. Select transmittals
2. Drag to folder
3. Or right-click > Move to Folder
        `,
            },
        ],
        keywords: ['folders', 'organize', 'categories'],
        route: '/transmittals',
        relatedArticles: ['transmittals.overview'],
    },
];
